/**
 * Resolving a clicked/hovered DOM node back to a real block/column/widget id — §7 of
 * `ai-mcp-plan.md` ("Клик по элементу в preview → id").
 *
 * Every structural wrapper in the page canvas carries its real entity id as a dedicated
 * `data-raw-id` attribute (`default-block.component.html`, `default-column.component.html`,
 * `default-section.component.html`, every widget wrapper variant) — added SPECIFICALLY for this,
 * deliberately separate from the plain `id` attribute those same elements also carry. `id` is
 * shared with unrelated concerns that make it unsafe to key resolution off: a user-set widget
 * `displayId` can be arbitrary text, Angular CDK's `cdkDropList` needs its own `id` for
 * `connectedTo` matching, and — found live 2026-09-10 auditing this exact code — a column's own
 * template puts `[id]="entity().id"` on TWO different elements at once (`.column-wrapper` and its
 * inner `cdkDropList`), a genuine duplicate DOM id. `data-raw-id` has exactly one job: it is the
 * raw entity id and nothing else, set in exactly one place per entity, so a lookup keyed off it
 * can't land on a decoy the way one keyed off `id` could.
 *
 * The one wrinkle is that a SECTION is also `[data-raw-id]`-tagged, and a section is not one of the
 * three types this feature exposes (block/column/widget) — so the walk does not stop at the first
 * `[data-raw-id]` it finds, it keeps climbing until one is recognized as belonging to one of the
 * three tracked collections. A click that lands on a section's own margin (between a block's edge
 * and its column) resolves to the containing BLOCK, not to a dead end.
 *
 * The climb is additionally bounded by `root` (the canvas wrapper): if NOTHING between the start
 * element and the canvas boundary matches, `.closest()` would otherwise keep climbing past the
 * canvas into the rest of the page — the header, the AI chat drawer, anything — on the (unlikely
 * but real) chance something out there also carries a `data-raw-id` that happens to match a known
 * id. `root` makes "found nothing in the canvas" the one way this returns null — never "kept
 * looking outside it and found something else instead".
 */

export type PickedEntityType = 'block' | 'column' | 'widget';

export interface PickedEntity {
    type: PickedEntityType;
    id: string;
}

export interface PickedEntityLookups {
    isWidgetId: (id: string) => boolean;
    isColumnId: (id: string) => boolean;
    isBlockId: (id: string) => boolean;
}

/**
 * Pure and DOM-only — no store, no Angular — so it can be unit-tested against a hand-built DOM
 * fragment instead of a full TestBed. `lookups` is injected rather than imported so the caller
 * decides where "is this a real id" comes from (the NgRx entity dictionaries, in production).
 */
const RAW_ID_ATTR = 'data-raw-id';
const RAW_ID_SELECTOR = `[${RAW_ID_ATTR}]`;

export function resolvePickedEntity(start: Element, lookups: PickedEntityLookups, root?: Element): PickedEntity | null {
    let candidate: Element | null = start.closest(RAW_ID_SELECTOR);

    while (candidate && (!root || root.contains(candidate))) {
        const id = candidate.getAttribute(RAW_ID_ATTR);

        if (id) {
            if (lookups.isWidgetId(id)) {
                return { type: 'widget', id };
            }

            if (lookups.isColumnId(id)) {
                return { type: 'column', id };
            }

            if (lookups.isBlockId(id)) {
                return { type: 'block', id };
            }
        }

        // Not one of the three tracked types (a section, or an unrelated id elsewhere in the DOM) —
        // keep climbing from the NEXT ancestor, not from `candidate` itself, or `.closest(...)`
        // would just return `candidate` again forever.
        candidate = candidate.parentElement ? candidate.parentElement.closest(RAW_ID_SELECTOR) : null;
    }

    return null;
}

/**
 * `@type:id` — the literal reference text inserted into the chat message and explained to the model
 * in `ai-chat-drawer.prompt-core.ts`. Shared with `entity-mention.util.ts`'s "@" autocomplete: both
 * the click-to-pick flow and typing "@block:…" by hand produce the exact same string.
 */
export function formatPickedEntityReference(entity: PickedEntity): string {
    return `@${entity.type}:${entity.id}`;
}
